/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

export type LayoutSectionKey =
    | 'dashboard-grid'
    | 'kpi-static'
    | 'kpi-api2-time-series'
    | 'kpi-api2-last-value'
    | 'api-data'
    | 'time-series-plot'
    | 'stored-plotly-plot'
    | 'mermaid-diagram'
    | 'markdown'
    | 'typography'
    | 'components'
    | 'icons';

export type LayoutSectionGroupKey =
    | 'foundations'
    | 'kpis-api-data'
    | 'visualization-content';

export interface LayoutSectionGroupConfig {
    description: string;
    key: LayoutSectionGroupKey;
    label: string;
}

export interface LayoutSectionConfig {
    badgeClassName: string;
    badgeLabel: string;
    description: string;
    group: LayoutSectionGroupKey;
    key: LayoutSectionKey;
    label: string;
    route: string;
}

export const LAYOUT_SECTION_GROUP_CONFIGS: LayoutSectionGroupConfig[] = [
    {
        key: 'foundations',
        label: 'Layout & Foundations',
        description: 'Dashboard layout primitives and typographic defaults.',
    },
    {
        key: 'kpis-api-data',
        label: 'KPIs & API Data',
        description: 'KPI cards and API data sources for monitoring values.',
    },
    {
        key: 'visualization-content',
        label: 'Visualization & Content',
        description: 'Charts, diagrams and structured content components.',
    },
];

export const LAYOUT_SECTION_CONFIGS: LayoutSectionConfig[] = [
    {
        key: 'dashboard-grid',
        group: 'foundations',
        label: 'DashboardGrid',
        badgeLabel: 'Layout',
        badgeClassName: 'badge-primary',
        description:
            'DashboardGrid renders responsive dashboard page layouts with configurable desktop tracks and item spans.',
        route: `${APP_ROUTES.layouts}/dashboard-grid`,
    },
    {
        key: 'typography',
        group: 'foundations',
        label: 'Typography',
        badgeLabel: 'Typography',
        badgeClassName: 'badge-secondary',
        description:
            'Typography shows the base tag styling for headings and paragraphs from the shared stylesheet layer.',
        route: `${APP_ROUTES.layouts}/typography`,
    },
    {
        key: 'components',
        group: 'foundations',
        label: 'Components',
        badgeLabel: 'Component',
        badgeClassName: 'badge-warning',
        description:
            'Selected reusable components with implementation examples and rendered previews.',
        route: `${APP_ROUTES.layouts}/components`,
    },
    {
        key: 'icons',
        group: 'foundations',
        label: 'Icons',
        badgeLabel: 'Reference',
        badgeClassName: 'badge-info',
        description:
            'Shared supply icons with standardized colors for consistent use across the application.',
        route: `${APP_ROUTES.layouts}/icons`,
    },
    {
        key: 'markdown',
        group: 'visualization-content',
        label: 'Markdown',
        badgeLabel: 'Content',
        badgeClassName: 'badge-accent',
        description:
            'Markdown renders lightweight formatted content such as headings, tables, and explanatory notes.',
        route: `${APP_ROUTES.layouts}/markdown`,
    },
    {
        key: 'mermaid-diagram',
        group: 'visualization-content',
        label: 'MermaidDiagram',
        badgeLabel: 'Diagram',
        badgeClassName: 'badge-info',
        description:
            'MermaidDiagram renders text-based diagrams such as flowcharts, sequence diagrams, and class diagrams.',
        route: `${APP_ROUTES.layouts}/mermaid-diagram`,
    },
    {
        key: 'kpi-static',
        group: 'kpis-api-data',
        label: 'KPI Static',
        badgeLabel: 'KPI',
        badgeClassName: 'badge-success',
        description:
            'KpiValueStatic and KpiCarouselStatic render manually provided KPI values.',
        route: `${APP_ROUTES.layouts}/kpi-static`,
    },
    {
        key: 'kpi-api2-time-series',
        group: 'kpis-api-data',
        label: 'KPI API2 Time Series',
        badgeLabel: 'KPI',
        badgeClassName: 'badge-success',
        description:
            'KpiValueApi2TimeSeries and KpiCarouselApi2TimeSeries load and aggregate API2 time series.',
        route: `${APP_ROUTES.layouts}/kpi-api2-time-series`,
    },
    {
        key: 'kpi-api2-last-value',
        group: 'kpis-api-data',
        label: 'KPI API2 Current Value',
        badgeLabel: 'KPI',
        badgeClassName: 'badge-success',
        description:
            'KpiValueApi2LastValue and KpiCarouselApi2LastValue load current API2 values directly.',
        route: `${APP_ROUTES.layouts}/kpi-api2-last-value`,
    },
    {
        key: 'api-data',
        group: 'kpis-api-data',
        label: 'API2 Data',
        badgeLabel: 'Data',
        badgeClassName: 'badge-neutral',
        description:
            'Api2LastValueData and Api2TimeSeriesData provide resolved API data for custom rendering.',
        route: `${APP_ROUTES.layouts}/api-data`,
    },
    {
        key: 'time-series-plot',
        group: 'visualization-content',
        label: 'TimeSeriesPlot',
        badgeLabel: 'Time Series',
        badgeClassName: 'badge-warning',
        description:
            'TimeSeriesPlot renders parsed time series data as shared Plotly figures from static or API data.',
        route: `${APP_ROUTES.layouts}/time-series-plot`,
    },
    {
        key: 'stored-plotly-plot',
        group: 'visualization-content',
        label: 'StoredPlotlyPlot',
        badgeLabel: 'Plotly',
        badgeClassName: 'badge-neutral',
        description:
            'StoredPlotlyPlot renders ready-made Plotly JSON figures from static input or API requests.',
        route: `${APP_ROUTES.layouts}/stored-plotly-plot`,
    },
];

export function getLayoutSectionByPathname(pathname: string): LayoutSectionConfig | null {
    return LAYOUT_SECTION_CONFIGS.find((section) => pathname === section.route) ?? null;
}

export function getLayoutSectionByKey(key: LayoutSectionKey): LayoutSectionConfig | null {
    return LAYOUT_SECTION_CONFIGS.find((section) => section.key === key) ?? null;
}
